import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Banner, BlockStack, Box, Button, Card,
  IndexTable, InlineStack, Select, Spinner, Text, useBreakpoints,
} from '@shopify/polaris';
import { useAxiosInstance } from '../../context/AxiosContext';
import { usePromotions, APPS, CARDS } from './api';
import StatusBadge from './StatusBadge';
import AppTags from './AppTags';

const STATE_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Published', value: 'published' },
  { label: 'Unpublished', value: 'unpublished' },
  { label: 'Archived', value: 'archived' },
];

const APP_FILTERS = [
  { label: 'All apps · newest first', value: '' },
  ...APPS.map(({ code, name }) => ({ label: name, value: code })),
];

const CardsList = ({ onEdit }) => {
  const axiosInstance = useAxiosInstance();
  const { smDown } = useBreakpoints();
  const [state, setState] = useState('');
  const [appCode, setAppCode] = useState('');

  const { data: cards, isLoading, isError } = usePromotions({
    resource: CARDS,
    params: { state, app_code: appCode },
    axiosInstance,
  });

  // "All apps" has no position to print, so the column is dropped rather than filled with dashes.
  const rows = (cards || []).map((card, index) => (
    <IndexTable.Row id={String(card.id)} key={card.id} position={index}>
      {appCode && (
        <IndexTable.Cell>
          <Text as="span" variant="bodySm" tone="subdued" numeric>
            {card.app_dashboard[appCode]?.position}
          </Text>
        </IndexTable.Cell>
      )}
      <IndexTable.Cell>
        <Text as="span" fontWeight="semibold">{card.app_name}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell><StatusBadge status={card.status} /></IndexTable.Cell>
      <IndexTable.Cell><AppTags codes={card.app_codes} /></IndexTable.Cell>
      <IndexTable.Cell>
        <Button size="slim" onClick={() => onEdit(card)}>Edit</Button>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const body = () => {
    if (isLoading) {
      return (
        <Box padding="600">
          <InlineStack align="center"><Spinner accessibilityLabel="Loading partner cards" /></InlineStack>
        </Box>
      );
    }

    if (isError) {
      return (
        <Box padding="400">
          <Banner tone="critical">Could not load partner cards. Refresh to try again.</Banner>
        </Box>
      );
    }

    if (!cards?.length) {
      return (
        <Box padding="600">
          <BlockStack gap="100" inlineAlign="center">
            <Text as="p" fontWeight="medium">Nothing matches this filter</Text>
            <Text as="p" tone="subdued">Try a different status or app.</Text>
          </BlockStack>
        </Box>
      );
    }

    return (
      <IndexTable
        resourceName={{ singular: 'partner card', plural: 'partner cards' }}
        itemCount={cards.length}
        selectable={false}
        condensed={smDown}
        headings={[
          ...(appCode ? [{ title: 'Pos' }] : []),
          { title: 'Partner' },
          { title: 'Status' },
          { title: 'Shown in' },
          { title: 'Actions' },
        ]}
      >
        {rows}
      </IndexTable>
    );
  };

  return (
    <BlockStack gap="400">
      <InlineStack gap="300">
        <Select label="Status" labelInline options={STATE_FILTERS} value={state} onChange={setState} />
        <Select label="Order for" labelInline options={APP_FILTERS} value={appCode} onChange={setAppCode} />
      </InlineStack>

      <Card padding="0">{body()}</Card>
    </BlockStack>
  );
};

CardsList.propTypes = {
  onEdit: PropTypes.func.isRequired,
};

export default CardsList;
